import { useState } from 'react';
import { useIntl } from 'react-intl';

import { AnonymousAuthGate } from '../components/AnonymousAuthGate.tsx';
import { AuthenticatedChrome } from '../components/AuthenticatedChrome.tsx';
import { PageShell } from '../components/PageShell.tsx';
import { canConnectPasskeyAccount } from '../lib/auth-capability.ts';
import { authClient } from '../lib/auth-client.ts';
import { authErrorMessageId } from '../lib/auth-error-message.ts';
import { resolveLocale } from '../../localization/intl.ts';
import { useAuthController } from '../lib/use-auth-controller.ts';
import { useAuthenticatedDecks } from '../lib/use-authenticated-decks.ts';
import type { DeckHierarchy } from '../lib/deck-page.ts';
import { DeckPage } from './DeckPage.tsx';
import { HomePage } from './HomePage.tsx';
import { PdfPage } from './PdfPage.tsx';

export function AuthPage() {
  const intl = useIntl();
  const controller = useAuthController();
  const { actionError, deckRoute, deckSlug, session, token } = controller;
  const [deckHierarchy, setDeckHierarchy] = useState<DeckHierarchy | null>(
    null,
  );
  const decksResource = useAuthenticatedDecks(session.data?.user.id ?? null);
  const errorMessageId = authErrorMessageId(actionError);
  const errorMessage = errorMessageId
    ? intl.formatMessage({ id: errorMessageId })
    : null;

  if (session.isPending)
    return (
      <PageShell
        projectName={
          deckSlug
            ? 'link'
            : window.location.pathname === '/'
              ? 'current'
              : 'link'
        }
      >
        {deckSlug ? (
          <DeckPage
            onHierarchyChange={setDeckHierarchy}
            scope={deckRoute?.scope}
            slug={deckSlug}
            session={session}
          />
        ) : (
          <>
            <p className="text-muted-foreground">
              {intl.formatMessage({ id: 'web.auth.checking' })}
            </p>
          </>
        )}
      </PageShell>
    );

  if (session.data) {
    const current = deckSlug
      ? 'deck'
      : window.location.pathname === '/pdf'
        ? 'pdf'
        : 'home';
    return (
      <AuthenticatedChrome
        actionError={actionError}
        decks={decksResource.data}
        decksError={decksResource.error}
        decksLoading={decksResource.isLoading}
        current={current}
        currentDeckSlug={deckSlug}
        currentDeckScope={deckRoute?.scope}
        currentDeckHierarchy={deckHierarchy}
        errorMessage={errorMessage}
        isActing={controller.isActing}
        name={session.data.user.name}
        onAddPasskey={() =>
          void controller.run(() => authClient.passkey.addPasskey())
        }
        onConnectPasskey={
          canConnectPasskeyAccount(session.data.user)
            ? () => void controller.startPasskeyMerge()
            : undefined
        }
        onConfirmPasskeyMerge={() => void controller.confirmPasskeyMerge()}
        onConfirmTelegramMerge={() => void controller.mergeTelegramAccount()}
        onDeclinePasskeyMerge={() => void controller.declinePasskeyMerge()}
        onDeclineTelegramMerge={controller.declineTelegramMerge}
        onRetryDecks={decksResource.refresh}
        onSignOut={() => void controller.run(() => authClient.signOut())}
        passkeyMergeError={controller.passkeyMergeError}
        passkeyMergeToken={controller.passkeyMergeToken}
        showTelegramMerge={Boolean(token) && Boolean(actionError)}
      >
        {deckSlug ? (
          <DeckPage
            onHierarchyChange={setDeckHierarchy}
            scope={deckRoute?.scope}
            slug={deckSlug}
            session={session}
          />
        ) : window.location.pathname === '/pdf' ? (
          <PdfPage />
        ) : (
          <HomePage decks={decksResource.data} error={decksResource.error} />
        )}
      </AuthenticatedChrome>
    );
  }

  if (deckSlug)
    return (
      <PageShell projectName="link">
        <DeckPage
          onHierarchyChange={setDeckHierarchy}
          scope={deckRoute?.scope}
          slug={deckSlug}
          session={session}
        />
      </PageShell>
    );

  return (
    <PageShell
      projectName={window.location.pathname === '/' ? 'current' : 'link'}
    >
      <AnonymousAuthGate
        errorMessage={errorMessage}
        isActing={controller.isActing}
        onSignIn={() => void controller.run(() => authClient.signIn.passkey())}
        onSignUp={() =>
          void controller.run(() =>
            authClient.signUp.passkey(
              resolveLocale(navigator.languages[0] ?? navigator.language),
            ),
          )
        }
        showActions={!token}
        token={token}
      />
    </PageShell>
  );
}
